// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface BulkAction {
  label: string;
  onClick: () => void;
  busy?: boolean;
  busyLabel?: string;
  primary?: boolean;
  testId?: string;
}

const SELECT_HINT = 'Select one or more rows';

/**
 * The /feedback bulk actions, on screen whether or not anything is selected.
 * With no selection each button is `aria-disabled` rather than `disabled`, so it
 * stays in the tab order and its tooltip can say why it does nothing.
 */
export function BulkActionToolbar({
  selectionCount,
  actions,
}: {
  selectionCount: number;
  actions: readonly BulkAction[];
}): React.JSX.Element {
  const inactive = selectionCount === 0;
  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-testid="feedback-bulk-toolbar"
        className="flex flex-wrap items-center gap-2 rounded-gl border border-border bg-muted/30 p-2"
      >
        <span className="px-1 text-[12px] text-muted-foreground">{selectionCount} selected</span>
        {actions.map((action) => {
          const button = (
            <Button
              key={action.label}
              size="sm"
              variant={action.primary ? 'default' : 'outline'}
              data-testid={action.testId}
              aria-disabled={inactive || action.busy ? true : undefined}
              className={cn(inactive && 'cursor-not-allowed opacity-50')}
              onClick={() => {
                if (inactive || action.busy) return;
                action.onClick();
              }}
            >
              {action.busy && action.busyLabel ? action.busyLabel : action.label}
            </Button>
          );
          if (!inactive) return button;
          return (
            <Tooltip key={action.label}>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent>{SELECT_HINT}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
